import pluralize from 'pluralize';
import { memo, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { type SavedSize } from '@air/feature-saved-sizes/utils';
import { Banner } from '@air/primitive-banner';
import { Field, FieldLabel } from '@air/primitive-field';
import { RadioGroup, RadioGroupItem } from '@air/primitive-radio-group';

import { type BatchExportImagesFormInput } from '~/components/ExportImagesModal/BatchExportImagesModal';
import { BatchExportSizeInputGroup } from '~/components/ExportImagesModal/components/BatchExportSizeInputGroup';
import { ExportSavedSizeSelect } from '~/components/ExportImagesModal/components/ExportImagesModalForm/ExportSavedSizeSelect';

const CROP_ANCHOR_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Corner', value: 'corner' },
] as const;

export type BatchExportExactSizeControlsProps = {
  formId: string;
  cappedCount: number;
  sizePresets: SavedSize[];
  totalCount: number;
};

/**
 * Controls for the "Exact size" export mode: the saved-size quick-pick, the W/H input group, the
 * crop-from anchor, and the helper/warning copy. Reads the target dimensions, crop anchor, and
 * validation errors from form context, so only the derived, non-form counts (`cappedCount`/
 * `totalCount`) and `sizePresets` are passed as props.
 */
export const BatchExportExactSizeControls = memo(
  ({ formId, cappedCount, sizePresets, totalCount }: BatchExportExactSizeControlsProps) => {
    const {
      control,
      setValue,
      formState: { errors },
    } = useFormContext<BatchExportImagesFormInput>();
    const width = useWatch({ control, name: 'targetWidth' });
    const height = useWatch({ control, name: 'targetHeight' });
    const cropAnchor = useWatch({ control, name: 'cropAnchor' });

    // Identity for the saved-size select has to be the preset id, not its dimensions, since two presets can share a width/height.
    const [selectedSavedSizeId, setSelectedSavedSizeId] = useState<string | null>(null);
    const selectedSavedSize = sizePresets.find((preset) => preset.id === selectedSavedSizeId);
    const isSelectedSavedSizeValid =
      !!selectedSavedSize && selectedSavedSize.width === width && selectedSavedSize.height === height;

    const hasError = !!errors.targetWidth || !!errors.targetHeight;
    const errorMessage = errors.targetWidth?.message ?? errors.targetHeight?.message;
    const errorId = `${formId}-target-size-error`;
    const hasValidTargetSize = !hasError && width != null && height != null;

    const handleCropAnchorChange = useCallback(
      (value: string) => {
        // Narrow the raw radio string to the CropAnchor union before writing it back to the form.
        if (value === 'center' || value === 'corner') {
          setValue('cropAnchor', value, { shouldValidate: true });
        }
      },
      [setValue],
    );

    const handleSelectSavedSize = useCallback(
      (savedSize: SavedSize) => {
        setSelectedSavedSizeId(savedSize.id);
        setValue('targetWidth', savedSize.width, { shouldValidate: true });
        setValue('targetHeight', savedSize.height, { shouldValidate: true });
      },
      [setValue],
    );

    return (
      <div className="flex flex-col gap-3">
        {sizePresets.length > 0 && (
          <ExportSavedSizeSelect
            onSelect={handleSelectSavedSize}
            selectedId={isSelectedSavedSizeValid && selectedSavedSizeId ? selectedSavedSizeId : ''}
            sizePresets={sizePresets}
          />
        )}
        <Field>
          <FieldLabel htmlFor={`${formId}-targetWidth`}>Dimensions</FieldLabel>
          <BatchExportSizeInputGroup errorId={errorId} formId={formId} hasError={hasError} />
          {hasError && (
            // A flow (non-absolute) element so the error reserves layout space and doesn't
            // overlap the next section, matching the helper copy below it.
            <div className="mt-1 ads:text-error text-12 text-red-9" id={errorId}>
              {errorMessage}
            </div>
          )}
        </Field>

        <Field>
          <FieldLabel id={`${formId}-cropAnchor-label`}>Crop from</FieldLabel>
          <RadioGroup
            aria-labelledby={`${formId}-cropAnchor-label`}
            className="flex-row gap-4"
            onValueChange={handleCropAnchorChange}
            value={cropAnchor}
          >
            {CROP_ANCHOR_OPTIONS.map((option) => (
              <RadioGroupItem id={`${formId}-cropAnchor-${option.value}`} key={option.value} value={option.value}>
                {option.label}
              </RadioGroupItem>
            ))}
          </RadioGroup>
        </Field>

        {!hasError && cappedCount > 0 && (
          <Banner appearance="warning" size="small">
            {cappedCount} of {totalCount} {pluralize('asset', totalCount)} {cappedCount === 1 ? 'is' : 'are'} smaller
            than {width}×{height}px and will export at the largest size that fits this ratio, without upscaling.
          </Banner>
        )}

        {hasValidTargetSize && (
          <div className="ads:text-metadata text-12 text-gray-10">
            Each asset will be cropped to fill {width}×{height}px. Results will vary based on each asset&apos;s original
            dimensions.
          </div>
        )}
      </div>
    );
  },
);

BatchExportExactSizeControls.displayName = 'BatchExportExactSizeControls';
